<script lang="ts" setup>
    import { onMounted, ref } from 'vue'
    import { useGoogleDriveStore } from '~~/stores/google-drive'

    const props = defineProps<{
        modelValue: string | null
        identityId: number
        showFiles?: boolean
        rootFolderId?: string | null
    }>()

    const emit = defineEmits<{
        (e: 'update:modelValue', value: string): void
    }>()

    interface DriveNode {
        id: string
        name: string
        mimeType: string
        isFolder: boolean
        children: DriveNode[]
        loading: boolean
        loaded: boolean
    }

    const driveStore = useGoogleDriveStore()
    const FOLDER_MIME = 'application/vnd.google-apps.folder'

    const nodes = ref<DriveNode[]>([])
    const rootLoading = ref(false)
    const forceExpandIds = ref<string[]>([])

    function fileIcon(mimeType: string): string {
        if (mimeType === FOLDER_MIME) {
            return 'folder'
        }
        if (mimeType.startsWith('image/')) {
            return 'image'
        }
        if (mimeType === 'application/pdf') {
            return 'picture_as_pdf'
        }
        if (mimeType.startsWith('video/')) {
            return 'videocam'
        }
        return 'insert_drive_file'
    }

    function toNode(item: any): DriveNode {
        return {
            id: item.id,
            name: item.name,
            mimeType: item.mimeType,
            isFolder: item.isFolder,
            children: [],
            loading: false,
            loaded: false,
        }
    }

    async function onExpand(node: DriveNode) {
        if (!node.isFolder || node.loaded) {
            return
        }
        node.loading = true
        const items = await driveStore.fetchFolderChildren(node.id, props.showFiles)
        node.children = items.map(toNode)
        node.loaded = true
        node.loading = false
    }

    function onSelect(node: DriveNode) {
        emit('update:modelValue', node.id)
    }

    onMounted(async () => {
        const startId = props.rootFolderId ?? 'root'
        rootLoading.value = true
        const items = await driveStore.fetchFolderChildren(startId, props.showFiles)
        nodes.value = items.map(toNode)
        rootLoading.value = false

        if (props.modelValue) {
            try {
                const ancestors = await driveStore.fetchFolderAncestors(props.modelValue)
                forceExpandIds.value = ancestors.filter((id) => id !== startId)
            }
            catch {
                // Silently ignore — picker still works without auto-expansion
            }
        }
    })
</script>

<template>
    <div style="min-height: 200px; max-height: 400px; overflow-y: auto">
        <q-inner-loading :showing="rootLoading">
            <q-spinner
                color="primary"
                size="32px"
            />
        </q-inner-loading>

        <template v-if="!rootLoading">
            <google-drive-picker-node
                v-for="node in nodes"
                :key="node.id"
                :node="node"
                :depth="0"
                :selected-id="modelValue"
                :show-files="showFiles ?? false"
                :force-expand-ids="forceExpandIds"
                @select="onSelect"
                @expand="onExpand"
            />
        </template>
    </div>
</template>

<style scoped></style>
